import { useState, useEffect, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, Progress, Checkbox, Label } from '@creanizer/ui'
import { AlertCircle } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import type { RelationshipInput, RelationshipResult } from '@creanizer/types'
import { RelationshipForm } from './RelationshipForm'
import { RelationshipGraph } from './RelationshipGraph'
import { SavedRelationshipsList } from './SavedRelationshipsList'

export function RelationshipGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { savedRelationships } = useRelationshipStore()
  const generator = useGenerator('relationship')
  const streamGenerator = useStreamGenerator('relationship')
  
  const [input, setInput] = useState<RelationshipInput>({
    numberOfCharacters: 5,
    complexityLevel: 5
  })
  const [result, setResult] = useState<RelationshipResult | null>(null)
  const [error, setError] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState(true)
  const [activeTab, setActiveTab] = useState('generate')
  const resultRef = useRef<HTMLDivElement>(null)

  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }

    setError('')
    setResult(null)

    try {
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              // charactersとrelationshipsが存在することを確認
              if (typeof resultData !== 'object' || 
                  !('characters' in resultData) || !('relationships' in resultData)) {
                console.error('Invalid result structure:', resultData)
                setError('生成結果にキャラクターまたは関係性データが含まれていません')
                return
              }
              
              setResult(resultData as RelationshipResult)
              
              // 結果が表示されたら自動的にスクロール
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        console.log('API Response:', response) // デバッグ用ログ
        
        // レスポンス構造の確認
        if (!response) {
          throw new Error('APIから有効なレスポンスが返されませんでした')
        }
        
        // resultがある場合はそれを使用、なければresponse全体を使用
        const resultData = response.result || response
        
        // charactersとrelationshipsが存在することを確認
        if (typeof resultData !== 'object' || resultData === null || 
            !('characters' in resultData) || !('relationships' in resultData)) {
          console.error('Invalid result structure:', resultData)
          throw new Error('生成結果にキャラクターまたは関係性データが含まれていません')
        }
        
        setResult(resultData as RelationshipResult)
        
        // 結果が表示されたら自動的にスクロール
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      console.error('Generation error:', err)
      setError(err instanceof Error ? err.message : '生成中にエラーが発生しました')
    }
  }

  const isSaved = () => {
    if (!result || !result.characters || !result.relationships) return false
    
    // メインキャラクター名で保存済みかチェック
    const mainCharacter = result.characters.find(c => c.isMain)
    return savedRelationships.some(saved => {
      const savedMain = saved.result.characters?.find(c => c.isMain)
      return savedMain?.name === mainCharacter?.name
    })
  }
  
  const handleSave = () => {
    if (!result || !result.characters || !result.relationships) {
      console.error('Cannot save: Invalid result structure', result)
      return
    }
    
    const mainCharacter = result.characters.find(c => c.isMain)
    const title = mainCharacter?.name 
      ? `${mainCharacter.name}の人間関係`
      : `${result.characters.length}人の人間関係`
    
    useRelationshipStore.getState().addRelationship({
      title,
      input,
      result
    })
    // 状態を更新するために新しいオブジェクトで再レンダリング
    setResult({...result})
  }

  const handleLoad = (relationship: any) => {
    setInput(relationship.input)
    setResult(relationship.result)
    setActiveTab('generate')
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-6">人間関係ジェネレータ</h2>
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">生成</TabsTrigger>
              <TabsTrigger value="saved">
                保存済み ({savedRelationships.length}/200)
              </TabsTrigger>
            </TabsList>

            <TabsContent value="generate" className="space-y-4">
              {/* ストリーミング設定 */}
              <div className="flex items-center space-x-2 mb-4">
                <Checkbox
                  id="useStreaming"
                  checked={useStreaming}
                  onCheckedChange={(checked) => setUseStreaming(checked as boolean)}
                />
                <Label htmlFor="useStreaming" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  ストリーミングを使用（リアルタイム生成・進捗表示）
                </Label>
              </div>

              <RelationshipForm
                input={input}
                onChange={setInput}
                onGenerate={handleGenerate}
                isGenerating={generator.isPending || streamGenerator.isStreaming}
              />

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* ストリーミング進捗表示 */}
              {streamGenerator.isStreaming && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>生成中...</span>
                    <span>{Math.round(streamGenerator.progress)}%</span>
                  </div>
                  <Progress value={streamGenerator.progress} />
                  {streamGenerator.partialContent && (
                    <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {streamGenerator.partialContent.slice(-500)}
                        {streamGenerator.partialContent.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {result && (
                <div ref={resultRef}>
                  <RelationshipGraph
                    result={result}
                    onSave={handleSave}
                    isSaved={isSaved()}
                  />
                </div>
              )}
            </TabsContent>

            <TabsContent value="saved">
              <SavedRelationshipsList
                relationships={savedRelationships}
                onLoad={handleLoad}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}